import React from "react"
import { Modal, Tag } from "antd"
import {
  formatFeatureLabel,
  getOrganizationGenerationMedia,
} from "../../utils/organizationApiHelpers"
import { translations } from "../../utils/Translation/staticTranslation"
import { getAppLanguage } from "../../utils/getAppLanguage"

const OrganizationGenerationDetailModal = ({ open, generation, onClose }) => {
  const lang = getAppLanguage()
  const t = translations[lang]?.organizationPage?.generationModal

  if (!generation) return null

  const { inputUrl, outputUrls, prompt } =
    getOrganizationGenerationMedia(generation)

  const outputImageLabel = (index) =>
    t?.outputImage?.replace("{n}", String(index + 1)) ||
    `Output image ${index + 1}`
  const featureLabel = formatFeatureLabel(generation.feature, lang)
  const status = String(generation.gen_status || "").toLowerCase()
  const isFailed = status === "failed" || status === "error"

  const formatDate = (iso) => {
    if (!iso) return "—"
    try {
      return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch {
      return iso
    }
  }

  const renderImagePanel = (url, label, alt) => {
    if (!url) {
      return (
        <div className="org-gen-modal-panel org-gen-modal-panel--empty">
          <div className="org-gen-modal-panel-label">{label}</div>
          <div className="org-gen-modal-placeholder">{t?.noImage}</div>
        </div>
      )
    }
    return (
      <div className="org-gen-modal-panel">
        <div className="org-gen-modal-panel-label">{label}</div>
        <div className="org-gen-modal-image-wrap">
          <img src={url} alt={alt} className="org-gen-modal-image" />
        </div>
      </div>
    )
  }

  const renderOutputsPanel = () => {
    if (!outputUrls.length) {
      return renderImagePanel(null, t?.output, t?.outputAlt)
    }

    if (outputUrls.length === 1) {
      return renderImagePanel(
        outputUrls[0],
        outputImageLabel(0),
        prompt || t?.outputAlt,
      )
    }

    return (
      <div className="org-gen-modal-outputs">
        {outputUrls.map((url, index) => (
          <React.Fragment key={`${url}-${index}`}>
            {renderImagePanel(
              url,
              outputImageLabel(index),
              `${prompt || t?.outputAlt} ${index + 1}`,
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }

  const renderInputPanel = () => {
    if (inputUrl) {
      return renderImagePanel(inputUrl, t?.input, t?.inputAlt)
    }
    if (prompt) {
      return (
        <div className="org-gen-modal-panel">
          <div className="org-gen-modal-panel-label">{t?.input}</div>
          <div className="org-gen-modal-prompt-box">
            <span className="org-gen-modal-prompt-label">{t?.prompt}</span>
            <p className="org-gen-modal-prompt-text">{prompt}</p>
          </div>
        </div>
      )
    }
    return (
      <div className="org-gen-modal-panel org-gen-modal-panel--empty">
        <div className="org-gen-modal-panel-label">{t?.input}</div>
        <div className="org-gen-modal-placeholder">{t?.noInput}</div>
      </div>
    )
  }

  return (
    <Modal
      className="org-modal org-generation-detail-modal"
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      centered
      destroyOnClose
      title={
        <div className="org-gen-modal-header">
          <div>
            <div className="org-gen-modal-title">{featureLabel}</div>
            <div className="org-gen-modal-subtitle">
              {generation.member_email}
            </div>
          </div>
          <div className="org-gen-modal-tags">
            {isFailed ? (
              <Tag color="red">{generation.gen_status}</Tag>
            ) : (
              <Tag color="green">{generation.gen_status || t?.completed}</Tag>
            )}
            <span className="org-gen-modal-date">
              {formatDate(generation.time_created)}
            </span>
          </div>
        </div>
      }
    >
      {isFailed ? (
        <div className="org-gen-modal-failed">
          {generation.reason || generation.error || t?.generationFailed}
        </div>
      ) : null}

      <div className="org-gen-modal-io-row">
        {renderInputPanel()}
        {renderOutputsPanel()}
      </div>

      {inputUrl && prompt ? (
        <div className="org-gen-modal-footer-prompt">
          <span className="org-gen-modal-prompt-label">{t?.prompt}</span>
          <p className="org-gen-modal-prompt-text">{prompt}</p>
        </div>
      ) : null}
    </Modal>
  )
}

export default OrganizationGenerationDetailModal
